import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import JsonLd from '@/components/feature/JsonLd';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold font-heading text-foreground-950 mb-4">文章不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    image: article.image,
    author: {
      '@type': 'Organization',
      name: '盛世娱乐',
    },
    publisher: {
      '@type': 'Organization',
      name: '盛世娱乐',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://1616e.cn/article/${slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://1616e.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.categoryName,
        item: `https://1616e.cn/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 盛世娱乐`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://1616e.cn/article/${slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogImage={article.image}
      />
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />

      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: article.categoryName, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          {/* Article Header */}
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full hover:bg-secondary-200 transition-colors duration-200 cursor-pointer"
              >
                {article.categoryName}
              </Link>
              <span className="text-sm text-foreground-500">{article.date}</span>
              <span className="text-sm text-foreground-400">·</span>
              <span className="text-sm text-foreground-500">{article.readTime}</span>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <p className="text-base text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>

            <div className="flex flex-wrap gap-2 mt-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full border border-background-200/60"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </header>

          {/* Featured Image */}
          <div className="relative w-full h-[400px] md:h-[500px] rounded-xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Back to Category */}
          <div className="mt-12 pt-8 border-t border-background-200/60">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span>返回{article.categoryName}分类</span>
            </Link>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-16 bg-background-100/50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs text-foreground-500">{related.date}</span>
                        <span className="text-xs text-foreground-500">·</span>
                        <span className="text-xs text-foreground-500">{related.readTime}</span>
                      </div>
                      <h3 className="text-sm font-bold font-heading text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {related.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <style>{`
        .prose-custom h2 {
          font-size: 1.5rem;
          font-weight: 700;
          font-family: var(--font-heading), 'Noto Serif SC', serif;
          margin-top: 2rem;
          margin-bottom: 1rem;
          color: oklch(var(--foreground-950));
        }
        .prose-custom h3 {
          font-size: 1.25rem;
          font-weight: 600;
          font-family: var(--font-heading), 'Noto Serif SC', serif;
          margin-top: 1.75rem;
          margin-bottom: 0.75rem;
          color: oklch(var(--foreground-900));
        }
        .prose-custom p {
          margin-bottom: 1.25rem;
          line-height: 1.85;
          font-size: 0.95rem;
          color: oklch(var(--foreground-700));
        }
        .prose-custom strong {
          color: oklch(var(--primary-600));
          font-weight: 600;
        }
      `}</style>
    </>
  );
}